/* eslint-disable @typescript-eslint/naming-convention */
import { debounce } from 'lodash';
import MixpanelUtils from './MixpanelUtils';
import {
  resolveMixpanelInboundSource,
  resolveMixpanelReferrerProperties,
} from './inboundEntryTracking';
import { ACCOUNT_TYPES } from '@/constants/Home';
import { CHECKOUT_SECTION_COMPLETED_NAMES } from '@/constants/Order';
import {
  CHECKOUT_PAYMENT_METHOD_TO_TYPE,
  Page,
  Status,
  type TextBlastSection,
  type TextBlastSentType,
  type TextBlastSetupOption,
} from '@/constants/MixpanelEventData';
import type { IUser } from '@/common/interfaces/user.interface';
import { getDtAbTestVersionForMixpanel } from '@/utilities/FeatureUtils';

/** Mixpanel Source / type-gate value for the regular cart surface. */
const MIXPANEL_CART_SURFACE = 'Cart' as const;
/** Mixpanel Section value for stock-alert events fired from the HQ cart. */
const MIXPANEL_HQ_CART_SECTION = 'HQ Cart' as const;

/**
 * Fire the event to track the HQ View event.
 *
 * @param page  - The current and active page.
 * @param status - The most recent status of the page.
 */
export const captureHQViewEvent = debounce((page: string, status: string) => {
  MixpanelUtils.sendEvent('HQ Viewed', {
    Page: Page[page as keyof typeof Page],
    'Recent Status': Status[status as keyof typeof Status],
    DT: getDtAbTestVersionForMixpanel(),
  });
}, 300);

/**
 * Fire the event to track the HQ View event.
 *
 * @param email  - The current user email.
 */
export const captureHQVisitedEvent = (email: string) => {
  MixpanelUtils.sendEvent('HQ Visited', {
    Email: email,
  });
};

export const pageLoadedEvent = async (page: string) => {
  const entrySource = resolveMixpanelInboundSource();
  MixpanelUtils.sendEvent('Page Loaded', {
    Page: page,
    Source: entrySource ?? page,
    ...resolveMixpanelReferrerProperties(),
  });
};

/**
 * Fire the event to track when the user submits the form.
 *
 * @param type - The type of the form. Order Review, Request Change, Invite Member
 * @param id - Proof Item Id | Order Id | Account Id
 */
export const captureFormSubmitted = (type: string, id: string | number) => {
  MixpanelUtils.sendEvent('Form Submitted', {
    Source: 'HQ',
    Type: type,
    Rating: null,
    Id: id,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track when the user views the form.
 *
 * @param type - The type of the form. Order Review, Request Change, Invite Member
 */
export const captureFormViewed = (type: string) => {
  MixpanelUtils.sendEvent('Form Viewed', {
    Source: 'HQ',
    Type: type,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track when the user favorites a design or product.
 *
 * @param type - Design | Product
 * @param styleCode - Style Code | Design ID
 * @param action - Add | Remove
 * @param title - Title of the design or product
 */
export const captureFavoriteInfo = (
  type: string,
  styleCode: string,
  action: string,
  title: string
) => {
  MixpanelUtils.sendEvent(action === 'add' ? 'Favorite Added' : 'Favorite Removed', {
    Source: 'HQ Favorites',
    Type: type,
    Id: styleCode,
    Name: title ?? null,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track when the user try to download a design or CSV file.
 *
 * @param file - Design | Personalized Names CSV | Shipping Addresses CSV | Invoice | Participants List
 * @param id - Proof Item ID | Order Id | Invoice Id
 * @param version - If true, then the version of the proof item Id
 */
export const captureFileDownloaded = (file: string, id: string | number, version?: string) => {
  const EVENT_DATA: {
    Source: string;
    File: string;
    Id: string | number;
    DT: string;
    Version?: string;
  } = {
    Source: 'HQ',
    File: file,
    Id: id,
    DT: getDtAbTestVersionForMixpanel(),
  };

  if (version) {
    EVENT_DATA.Version = version;
  }

  MixpanelUtils.sendEvent('File Downloaded', EVENT_DATA);
};

/**
 * Fire the event to track the user try to reopen the store.
 *
 * @param id - Order Id.
 */
export const captureStoreReopnRequested = (id: number) => {
  MixpanelUtils.sendEvent('Store Reopen Requested', {
    Id: id,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track the user try to open and select the payement method.
 *
 * @param id - Invoice Id.
 * @param method - Payment Method. Credit Card | Zelle | Wire Transfer | Cheque
 */
export const capturePaymentSectionViewed = (id: number, method: string) => {
  MixpanelUtils.sendEvent('Payment Section Viewed', {
    Id: id,
    Source: 'Invoice',
    'Payment Method': method,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track the user try to edit the account section.
 *
 * @param category - Personal Info; Org Info; Members
 * @param fields - Name; Phone; Email; Password; Grad Year; School; Organization;
 */
export const captureAccountEdited = (category: string, fields: string) => {
  MixpanelUtils.sendEvent('Account Edited', {
    Category: category,
    Edit: fields,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track the user try to edit the account member section.
 *
 * @param edit - Add | Remove | Archieve
 */
export const captureAccountMemberEdited = (edit: string) => {
  MixpanelUtils.sendEvent('Account Edited', {
    Category: 'Members',
    Edit: edit,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track the user try to edit only the member section.
 *
 * @param accountType - Collegiate | Corporate | Other
 * @param editType - Add | Remove | Archieve
 * @param user - User object
 * @param memberId - Member identifier.
 */
export const captureMemberEdited = (
  accountType: string,
  editType: string,
  user: IUser,
  memberId?: number
) => {
  const eventProperties = {
    Edit: editType,
    'Requestor ID': user.id,
    'Member ID': editType === 'Add' ? null : memberId,
    'Organization ID': null as number | null,
    Organization: null as string | null,
    Business: null as string | null,
    'School ID': null as number | null,
    School: null as string | null,
    DT: getDtAbTestVersionForMixpanel(),
  };

  if (accountType === ACCOUNT_TYPES.COLLEGIATE) {
    eventProperties['School ID'] = user.school_id;
    eventProperties.School = user.school;
    eventProperties['Organization ID'] = user.organization_id;
    eventProperties.Organization = user.organization;
  } else if (accountType === ACCOUNT_TYPES.CORPORATE) {
    eventProperties.Business = user.business;
  } else if (accountType === ACCOUNT_TYPES.OTHER) {
    eventProperties['Organization ID'] = user.organization_id;
    eventProperties.Organization = user.organization;
  }

  MixpanelUtils.sendEvent('Member Edited', eventProperties);
};

/**
 * Fire the event to track the user try to add a design to cart or store.
 *
 * @param type - Store | Cart
 * @param id - Design id when adding to store; proof item id when adding to cart
 * @param version - Version of the proof item (cart only).
 */
export const captureAddedToStoreOrCart = (type: string, id: string | number, version?: string) => {
  if (type === 'Store') {
    MixpanelUtils.sendEvent('Added to Store', {
      Id: id,
      'Site Type': 'HQ',
      Source: 'HQ',
      Type: 'Product',
    });
    return;
  }

  MixpanelUtils.sendEvent(`Added to ${type}`, {
    Source: 'HQ',
    Version: version ?? null,
    Id: id,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track the user try to remove a design from cart or store.
 *
 * @param type - Store | cart
 * @param id - The proof Item Id (123A, 123B)
 * @param version - Version of the proof item.
 */
export const captureRemovedFromStoreOrCart = (type: string, id: string, version?: string) => {
  MixpanelUtils.sendEvent(`Removed from ${type}`, {
    Source: 'HQ',
    'Site Type': 'HQ',
    ...(type === MIXPANEL_CART_SURFACE && { Type: 'Regular Cart' }),
    Version: version ?? null,
    Id: id,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track the user try to empties all designs from cart or store.
 *
 * @param type - Store | cart
 * @param id - The proof Item Id (123A, 123B)
 * @param version - Version of the proof item.
 */
export const captureStoreOrCartEmptied = (type: string, id: string, version?: string) => {
  MixpanelUtils.sendEvent(`${type} Emptied`, {
    Source: 'HQ',
    'Site Type': 'HQ',
    ...(type === MIXPANEL_CART_SURFACE && { Type: 'Regular Cart' }),
    Version: version ?? null,
    Id: id,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

export const captureCartViewed = (
  id: string | null,
  count: number,
  productIds: string,
  productBrands: string,
  productColors: string,
  version?: string,
  designIds?: string,
  proofIds?: string,
  proofItemIds?: string
) => {
  MixpanelUtils.sendEvent('Cart Viewed', {
    Id: id,
    Version: version ?? null,
    Count: count,
    Source: 'HQ',
    'Site Type': 'HQ',
    Type: 'Regular Cart',
    DT: getDtAbTestVersionForMixpanel(),
    'Product Style Code': productIds,
    'Product Brand': productBrands,
    'Product Color': productColors,
    'Design ID': designIds ?? null,
    'Proof ID': proofIds ?? null,
    'Proof Item ID': proofItemIds ?? null,
  });
};

interface IProductQuantityChangedProps {
  name: string;
  productStyleCode: string;
  color: string;
  quantity: number;
  size: string;
}

/**
 * Fires when the user changes product quantity in the regular cart.
 * Used by CartSizeInputs for per-size and single-quantity inputs.
 */
export const captureProductQuantityChanged = ({
  name,
  productStyleCode,
  color,
  quantity,
  size,
}: IProductQuantityChangedProps) => {
  MixpanelUtils.sendEvent('Product Quantity Changed', {
    Name: name,
    'Product Style Code': productStyleCode,
    Color: color,
    Quantity: quantity,
    Size: size,
    'Site Type': 'HQ',
    Source: MIXPANEL_CART_SURFACE,
    Type: 'Product',
    DT: getDtAbTestVersionForMixpanel(),
  });
};

interface ISizeChartClickedProps {
  name: string;
  productStyleCode: string;
  color: string;
  productBrand: string;
}

/**
 * Fire when the user opens the size chart from regular cart size inputs.
 * Used by CartSizeInputs.
 */
export const captureCartSizeChartClicked = ({
  name,
  productStyleCode,
  color,
  productBrand,
}: ISizeChartClickedProps) => {
  MixpanelUtils.sendEvent('Size Chart Clicked', {
    Name: name,
    'Product Style Code': productStyleCode,
    'Site Type': 'HQ',
    Source: MIXPANEL_CART_SURFACE,
    Type: 'Product',
    Color: color,
    'Product Brand': productBrand,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

const STORES_CART_VIEWED_DEDUP_MS = 500;
let lastStoresCartViewedAt = 0;

export const resetStoresCartViewedTrackingForTests = () => {
  lastStoresCartViewedAt = 0;
};

/**
 * Fire the event when the user opens the store cart.
 */
export const captureStoresCartViewed = () => {
  const now = Date.now();

  if (now - lastStoresCartViewedAt < STORES_CART_VIEWED_DEDUP_MS) {
    return;
  }

  lastStoresCartViewedAt = now;

  MixpanelUtils.sendEvent('Stores Cart Viewed', {
    Source: 'HQ',
    'Site Type': 'HQ',
  });
};

/**
 * Fire the event when the user starts store checkout from the store cart.
 *
 * @param email - Logged-in user's email
 */
export const captureStoresFormViewed = (email: string) => {
  MixpanelUtils.sendEvent('Stores Form Viewed', {
    Email: email,
    'Site Type': 'HQ',
    Source: 'HQ',
  });
};

/**
 * Fire the event when the user completes a section of the stores checkout form.
 *
 * @param email - Logged-in user's email
 * @param section - The completed form section (e.g. Store Type, Store Info)
 * @param properties - Additional section-specific properties (e.g. Type, Name, Store Deadline)
 */
export const captureStoresFormSectionCompleted = (
  email: string,
  section: string,
  properties: Record<string, string> = {}
) => {
  MixpanelUtils.sendEvent('Stores Form Section Completed', {
    Email: email,
    Section: section,
    Source: 'HQ',
    'Site Type': 'HQ',
    ...properties,
  });
};

/**
 * Fire the event when the user completes store setup and launches the store.
 *
 * @param email - Logged-in user's email
 * @param storeId - Store ID
 */
export const captureStoresSetupCompleted = (email: string, storeId: number) => {
  MixpanelUtils.sendEvent('Stores Setup Completed', {
    Email: email,
    Id: storeId,
    Source: 'HQ',
    'Site Type': 'HQ',
  });
};

/**
 * Fire when an invite-members section is shown or finished.
 * Invite Members fires when that window opens. Add Manually and Add Bulk fire on Done.
 *
 * @param email - Organizer email
 * @param storeId - Store ID
 * @param section - Invite Members, Add Manually, or Add Bulk
 */
export const captureTextBlastSectionCompleted = (
  email: string,
  storeId: number,
  section: TextBlastSection
) => {
  MixpanelUtils.sendEvent('Text Blast Section Completed', {
    Email: email,
    Id: storeId,
    Section: section,
    Source: 'HQ',
    'Site Type': 'HQ',
  });
};

/**
 * Fire when a text blast (invite or reminder) is successfully sent.
 *
 * @param email - Organizer email
 * @param storeId - Store ID
 * @param count - Members selected to receive the message
 * @param type - Invite or Reminder
 * @param option - Auto if Sounds Good, Manual if I'll remind them myself or Send Reminder Now
 * @param category - Live Store on launch day, otherwise days left until close
 */
export const captureTextBlastSent = (
  email: string,
  storeId: number,
  count: number,
  type: TextBlastSentType,
  option: TextBlastSetupOption,
  category: string
) => {
  MixpanelUtils.sendEvent('Text Blast Sent', {
    Email: email,
    Id: storeId,
    Count: count,
    Type: type,
    Option: option,
    Category: category,
    Source: 'HQ',
    'Site Type': 'HQ',
  });
};

/**
 * Fire the event when the user saves changes on the store edit page.
 *
 * @param email - Logged-in user's email
 * @param storeId - Store ID
 */
export const captureStoreEdited = (email: string, storeId: number) => {
  MixpanelUtils.sendEvent('Stores Edited', {
    Email: email,
    Id: storeId,
    Source: 'HQ',
    'Site Type': 'HQ',
  });
};

/**
 * Fire the event when a store participant is edited and saved.
 *
 * @param email - Logged-in user's email
 * @param name - Participant name
 * @param storeId - Store ID
 * @param edit - Comma-separated list of edited fields (Address, Color, Size)
 */
export const captureStoresParticipantEdited = (
  email: string,
  name: string,
  storeId: number,
  edit: string
) => {
  MixpanelUtils.sendEvent('Stores Participant Edited', {
    Email: email,
    'Participant Name': name,
    Id: storeId,
    Edit: edit,
    Source: 'HQ',
    'Site Type': 'HQ',
  });
};

type PromoMixpanelSource = typeof MIXPANEL_CART_SURFACE | 'Checkout Page';

interface IPromoTrackingCartItem {
  apparelBrand: string;
  apparelHex: string;
  apparelStyleCode: string;
}

interface IPromoMixpanelProps {
  Id: number;
  'Promo Code': string;
  Source: PromoMixpanelSource;
  Type: 'Regular Cart' | 'Regular Order';
  'Product Brand': string;
  'Product Color Hex': string;
  'Site Type': 'HQ';
  'Product Style Code': string;
  DT: ReturnType<typeof getDtAbTestVersionForMixpanel>;
}

const buildPromoProductFields = (cartItems: IPromoTrackingCartItem[]) => ({
  productBrands: cartItems.map((item) => item.apparelBrand).join(';'),
  productColorHexes: cartItems.map((item) => item.apparelHex).join(';'),
  productStyleCodes: cartItems.map((item) => item.apparelStyleCode).join(';'),
});

type PromoMixpanelEventName = 'Promo Code Applied' | 'Promo Code Failed';

/**
 * Shared property shape for promo Mixpanel events (Applied / Failed).
 * Used by cart summary and Checkout V4 promo tracking.
 */
const buildPromoMixpanelProps = (
  id: number,
  promoCode: string,
  source: PromoMixpanelSource,
  productBrands: string,
  productColorHexes: string,
  productStyleCodes: string
): IPromoMixpanelProps => ({
  Id: id,
  'Promo Code': promoCode,
  Source: source,
  Type: source === MIXPANEL_CART_SURFACE ? 'Regular Cart' : 'Regular Order',
  'Product Brand': productBrands,
  'Product Color Hex': productColorHexes,
  'Site Type': 'HQ',
  'Product Style Code': productStyleCodes,
  DT: getDtAbTestVersionForMixpanel(),
});

const sendPromoMixpanelEvent = (
  eventName: PromoMixpanelEventName,
  id: number,
  promoCode: string,
  source: PromoMixpanelSource,
  cartItems: IPromoTrackingCartItem[]
) => {
  const { productBrands, productColorHexes, productStyleCodes } =
    buildPromoProductFields(cartItems);
  MixpanelUtils.sendEvent(
    eventName,
    buildPromoMixpanelProps(
      id,
      promoCode,
      source,
      productBrands,
      productColorHexes,
      productStyleCodes
    )
  );
};

/**
 * Fire the event to track when a promo code is applied.
 *
 * @param id - The promo code ID
 * @param promoCode - The promo code string
 * @param source - Where the promo was applied (Cart | Checkout Page)
 * @param productBrands - Semicolon-joined product brands
 * @param productColorHexes - Semicolon-joined product color hex codes
 * @param productStyleCodes - Semicolon-joined product style codes
 */
export const capturePromoCodeApplied = (
  id: number,
  promoCode: string,
  source: PromoMixpanelSource,
  productBrands: string,
  productColorHexes: string,
  productStyleCodes: string
) => {
  MixpanelUtils.sendEvent(
    'Promo Code Applied',
    buildPromoMixpanelProps(
      id,
      promoCode,
      source,
      productBrands,
      productColorHexes,
      productStyleCodes
    )
  );
};

/**
 * Fire when applying a promo code fails (API error, validation, guarantee required).
 * Used by cart summary and Checkout V4 apply handlers.
 */
export const capturePromoCodeFailed = (
  promoCode: string,
  source: PromoMixpanelSource,
  cartItems: IPromoTrackingCartItem[],
  id = 0,
  message = ''
) => {
  const { productBrands, productColorHexes, productStyleCodes } =
    buildPromoProductFields(cartItems);
  MixpanelUtils.sendEvent('Promo Code Failed', {
    ...buildPromoMixpanelProps(
      id,
      promoCode,
      source,
      productBrands,
      productColorHexes,
      productStyleCodes
    ),
    Message: message,
  });
};

/**
 * Fire the promo applied event for cart items.
 */
export const captureCartPromoCodeApplied = (
  promoDetails: { id: number; code: string },
  cartItems: IPromoTrackingCartItem[]
) => {
  sendPromoMixpanelEvent(
    'Promo Code Applied',
    promoDetails.id,
    promoDetails.code,
    MIXPANEL_CART_SURFACE,
    cartItems
  );
};

/**
 * Fire Promo Code Failed for the regular cart summary apply path.
 */
export const captureCartPromoCodeFailed = (
  promoCode: string,
  cartItems: IPromoTrackingCartItem[],
  id = 0,
  message = ''
) => {
  capturePromoCodeFailed(promoCode, MIXPANEL_CART_SURFACE, cartItems, id, message);
};

/**
 * Fire the promo applied event for checkout items.
 */
export const captureCheckoutPromoCodeApplied = (
  promoDetails: { id: number; code: string },
  cartItems: IPromoTrackingCartItem[]
) => {
  sendPromoMixpanelEvent(
    'Promo Code Applied',
    promoDetails.id,
    promoDetails.code,
    'Checkout Page',
    cartItems
  );
};

/**
 * Fire Promo Code Failed for Checkout V4 apply path.
 */
export const captureCheckoutPromoCodeFailed = (
  promoCode: string,
  cartItems: IPromoTrackingCartItem[],
  id = 0,
  message = ''
) => {
  capturePromoCodeFailed(promoCode, 'Checkout Page', cartItems, id, message);
};

/**
 * Store Events
 */
export const captureStoreFormViewed = () => {
  MixpanelUtils.sendEvent('Store Form Viewed', {
    Source: 'HQ',
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track the user try to complete the store form section.
 *
 * @param section - the section of the store form. (Store Details | Item Details | Shipping Details)
 */
export const captureStoreSectionCompleted = (section: string) => {
  MixpanelUtils.sendEvent('Store Form Section Completed', {
    Source: 'HQ',
    Section: section,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Method to track event when store is started.
 *
 * @param storeId - Store ID.
 * @param properties - Additional properties to be sent with the event. {
 *  Title - Title of the store
 *  Store Deadline - Store Deadline
 *  Product ID - Product Style Codes. (FP12;FP21;FP22;FP23;FP24)
 *  Cost Option - Fixed or Variable.
 *  Shipping Type - Individual or Bulk
 */
export const captureStoreStarted = (storeId: number, properties: Record<string, string>) => {
  MixpanelUtils.sendEvent('Store Started', {
    Source: 'HQ',
    Id: storeId,
    DT: getDtAbTestVersionForMixpanel(),
    ...properties,
  });
};

/** Checkout events. */

export const captureCheckoutViewed = () => {
  MixpanelUtils.sendEvent('Checkout Viewed', {
    Source: 'HQ',
    'Site Type': 'HQ',
    Type: 'Regular Order',
    DT: getDtAbTestVersionForMixpanel(),
  });
};

export type ManagerContactOption = 'Phone' | 'Email';

/**
 * Fire when the user taps the manager phone or email pill on Checkout V4.
 * Used by CheckoutV4Header contact links.
 */
export const captureManagerContactClicked = (option: ManagerContactOption) => {
  MixpanelUtils.sendEvent('Manager Contact Clicked', {
    Source: 'Checkout Page',
    'Site Type': 'HQ',
    Type: 'Regular Order',
    DT: getDtAbTestVersionForMixpanel(),
    Option: option,
  });
};

/**
 * Fire when the user changes shipping method in cart or checkout.
 * Reused event name with Source distinguishing Cart vs checkout surfaces.
 */
export const captureShippingMethodChanged = (
  shipTypeKey: string,
  source: typeof MIXPANEL_CART_SURFACE | 'Checkout Page' = 'Checkout Page'
) => {
  MixpanelUtils.sendEvent('Shipping Method Changed', {
    Source: source,
    'Site Type': 'HQ',
    Type: source === MIXPANEL_CART_SURFACE ? 'Regular Cart' : 'Regular Order',
    'Shipping Type': shipTypeKey,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire when the user changes shipping method on Checkout V4.
 * Used by CheckoutV4 ship method section / summary rail / mobile sheet.
 */
export const captureCheckoutShippingMethodChanged = (shipTypeKey: string) => {
  captureShippingMethodChanged(shipTypeKey, 'Checkout Page');
};

/**
 * Fire when the user opens the mobile Cart Summary bottom sheet.
 * Used by Cart footer "Cart Summary" toggle (mobile/tablet).
 */
export const captureOpenCartSummary = () => {
  MixpanelUtils.sendEvent('Cart Summary Viewed', {
    Source: 'HQ',
    'Site Type': 'HQ',
    Type: 'Regular Cart',
    DT: getDtAbTestVersionForMixpanel(),
  });
};

type CartCsvFileLabel = 'Numbers CSV' | 'Names CSV';

/**
 * Maps cart personalized CSV customization flags to the File property value.
 */
export const getCartCsvFileLabel = (
  hasCustomNames: boolean,
  hasCustomNumbers: boolean
): CartCsvFileLabel | null => {
  if (hasCustomNumbers) {
    return 'Numbers CSV';
  }
  if (hasCustomNames) {
    return 'Names CSV';
  }
  return null;
};

const buildCartCsvFileEventProps = (productStyleCode: string, file: CartCsvFileLabel) => ({
  Source: MIXPANEL_CART_SURFACE,
  'Site Type': 'HQ',
  Type: 'Regular Cart',
  'Product Style Code': productStyleCode,
  File: file,
  DT: getDtAbTestVersionForMixpanel(),
});

/**
 * Fire when the user downloads a personalized names/numbers CSV template from the cart.
 */
export const captureCartCsvFileDownloaded = (productStyleCode: string, file: CartCsvFileLabel) => {
  MixpanelUtils.sendEvent('File Downloaded', buildCartCsvFileEventProps(productStyleCode, file));
};

/**
 * Fire when the user successfully uploads a personalized names/numbers CSV from the cart.
 */
export const captureCartCsvFileUploaded = (productStyleCode: string, file: CartCsvFileLabel) => {
  MixpanelUtils.sendEvent('File Uploaded', buildCartCsvFileEventProps(productStyleCode, file));
};

const buildRegularOrderCsvFileEventProps = (productStyleCode: string, file: CartCsvFileLabel) => ({
  Source: 'HQ' as const,
  'Site Type': 'HQ' as const,
  Type: 'Regular Order' as const,
  'Product Style Code': productStyleCode,
  File: file,
  DT: getDtAbTestVersionForMixpanel(),
});

/** Fire when the user downloads a personalized CSV template during regular order customization. */
export const captureRegularOrderCsvFileDownloaded = (
  productStyleCode: string,
  file: CartCsvFileLabel
) => {
  MixpanelUtils.sendEvent(
    'File Downloaded',
    buildRegularOrderCsvFileEventProps(productStyleCode, file)
  );
};

/** Fire when the user uploads a personalized CSV during regular order customization. */
export const captureRegularOrderCsvFileUploaded = (
  productStyleCode: string,
  file: CartCsvFileLabel
) => {
  MixpanelUtils.sendEvent(
    'File Uploaded',
    buildRegularOrderCsvFileEventProps(productStyleCode, file)
  );
};

/**
 * Fire when the user dismisses the regular cart overlay.
 * Desktop: cart modal X. Mobile: header back button.
 * Used by RightSection cart Overlay close handlers.
 */
export const captureCartClosed = () => {
  MixpanelUtils.sendEvent('Cart Closed', {
    Source: 'HQ',
    'Site Type': 'HQ',
    Type: 'Regular Cart',
    DT: getDtAbTestVersionForMixpanel(),
  });
};

type EmptyCartCtaOption = 'Add Items' | 'Start Designing';

/**
 * Fire when the user clicks a CTA on an empty regular cart.
 * Option is Add Items (has completed designs) or Start Designing (no completed designs).
 */
export const captureEmptyCartCtaClicked = (option: EmptyCartCtaOption) => {
  MixpanelUtils.sendEvent('Empty Cart CTA Clicked', {
    Source: MIXPANEL_CART_SURFACE,
    'Site Type': 'HQ',
    Type: 'Regular Cart',
    Option: option,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire when the user changes shipping method in the regular cart summary.
 * Used by Cart handleShipTypeSelect.
 */
export const captureCartShippingMethodChanged = (shipTypeKey: string) => {
  captureShippingMethodChanged(shipTypeKey, MIXPANEL_CART_SURFACE);
};

/**
 * Fire when the user toggles Guarantee Delivery on or off.
 * Used by regular cart ShippingMethod and Checkout V4 guarantee controls.
 * Source distinguishes Cart vs Checkout Page; Type matches the surface.
 */
export const captureGuaranteeDeliveryChanged = (
  source: typeof MIXPANEL_CART_SURFACE | 'Checkout Page',
  guarantee: boolean
) => {
  MixpanelUtils.sendEvent('Guarantee Delivery Changed', {
    Source: source,
    'Site Type': 'HQ',
    Type: source === MIXPANEL_CART_SURFACE ? 'Regular Cart' : 'Regular Order',
    DT: getDtAbTestVersionForMixpanel(),
    Guarantee: guarantee,
  });
};

/**
 * Maps checkout payment method keys to Mixpanel Payment Type display values.
 */
const resolveCheckoutPaymentType = (paymentMethod: string): string =>
  CHECKOUT_PAYMENT_METHOD_TO_TYPE[paymentMethod] ?? paymentMethod;

/**
 * Resolves the payment method key sent on Checkout Completed from legacy checkout flows.
 * Pay Later maps to invoice; wallet/card selections pass through with card as fallback.
 */
export const resolveCheckoutCompletedPaymentMethod = (
  isPayLater: boolean,
  selectedPaymentMethod?: string | null
): string => (isPayLater ? 'invoice' : selectedPaymentMethod || 'card');

/**
 * Fire when the user selects a payment method on Checkout V4.
 * Used by CheckoutV4 PaymentDetailsSection (Card / PayPal / Google Pay / Venmo / Invoice).
 */
export const captureCheckoutPaymentMethodChanged = (paymentMethod: string) => {
  MixpanelUtils.sendEvent('Payment Method Changed', {
    Source: 'HQ',
    'Site Type': 'HQ',
    Type: 'Regular Order',
    'Payment Type': resolveCheckoutPaymentType(paymentMethod),
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire when the user explicitly cancels or abandons a Checkout V4 payment method.
 * Used for Card, PayPal, Google Pay, and Venmo cancellation signals.
 */
export const captureCheckoutPaymentCancelled = (paymentMethod: string) => {
  MixpanelUtils.sendEvent('Payment Cancelled', {
    'Payment Method': paymentMethod,
    Source: 'HQ',
    'Site Type': 'HQ',
    Type: 'Regular Order',
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire when Checkout V4 fails after Checkout Now (payment or order creation).
 * Failure Reason must be sanitized — never raw gateway/API text.
 * Used by CheckoutV4 payment authorization and createRegularOrder error paths.
 * Does not fire for UPS address validation, Payment Cancelled, or disabled-button states.
 */
export const captureCheckoutFailed = (paymentMethod: string, failureReason: string) => {
  MixpanelUtils.sendEvent('Checkout Failed', {
    'Payment Method': paymentMethod,
    'Failure Reason': failureReason,
    Source: 'HQ',
    'Site Type': 'HQ',
    Type: 'Regular Order',
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire when the user opens the mobile Order Summary sheet on Checkout V4.
 * Used by CheckoutV4 mobile dock footer toggle.
 */
export const captureCheckoutOrderSummaryViewed = () => {
  MixpanelUtils.sendEvent('Order Summary Viewed', {
    Source: 'HQ',
    'Site Type': 'HQ',
    Type: 'Regular Order',
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Shared properties for Checkout V4 design-note interaction events.
 */
const buildCheckoutDesignNoteProps = (cartItemId: number, productStyleCode: string) => ({
  Source: 'HQ' as const,
  'Site Type': 'HQ' as const,
  Type: 'Regular Order' as const,
  Id: cartItemId,
  'Product Style Code': productStyleCode,
  DT: getDtAbTestVersionForMixpanel(),
});

/**
 * Fire when the user opens the design-note editor for a line with no saved note.
 * Used by CheckoutV4 ReviewDesignsSection note field.
 */
export const captureCheckoutAddNote = (cartItemId: number, productStyleCode: string) => {
  MixpanelUtils.sendEvent(
    'Add Note Clicked',
    buildCheckoutDesignNoteProps(cartItemId, productStyleCode)
  );
};

export type CheckoutDesignNoteModifiedMethod = 'Save' | 'Edit' | 'Cancel';

/**
 * Fire when the user edits, saves, or cancels a design note on Checkout V4.
 * Used by CheckoutV4 ReviewDesignsSection note field.
 */
export const captureCheckoutDesignNoteModified = (
  cartItemId: number,
  productStyleCode: string,
  method: CheckoutDesignNoteModifiedMethod
) => {
  MixpanelUtils.sendEvent('Design Note Modified', {
    ...buildCheckoutDesignNoteProps(cartItemId, productStyleCode),
    Method: method,
  });
};

/**
 * Fire when the University Approval modal opens on Checkout V4.
 * Used by CheckoutV4 when collegiate approval is required before place order.
 * Legacy checkout has no modal (inline form), so this is V4-only.
 */
export const captureUniversityModalOpened = () => {
  MixpanelUtils.sendEvent('University Modal Viewed', {
    Source: 'HQ',
    'Site Type': 'HQ',
    Type: 'Regular Order',
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track the user try to complete the checkout form section.
 *
 * @param section - the section of the checkout form.
 * (Confirm Design | Shipping Details | Shipping Method | Payment Details | Info for University Approval)
 * @param paymentMethod - Checkout V4 payment method key; included as Payment Type when section is Payment Details.
 */
export const captureCheckoutSectionCompleted = (section: string, paymentMethod?: string) => {
  MixpanelUtils.sendEvent('Checkout Section Completed', {
    Source: 'HQ',
    'Site Type': 'HQ',
    Section: section,
    Type: 'Regular Order',
    DT: getDtAbTestVersionForMixpanel(),
    ...(section === CHECKOUT_SECTION_COMPLETED_NAMES.PAYMENT_DETAILS && paymentMethod
      ? { 'Payment Type': resolveCheckoutPaymentType(paymentMethod) }
      : {}),
  });
};

/**
 * Fire the event to track the user try to complete the checkout.
 *
 * @param orderId - The Order id
 * @param productIds - Total Product Ids (FP12;FP21;FP22;FP23;FP24)
 * @param subtotal - Total Subtotal
 * @param total - Total (Subtotal + Shipping + Sales tax)
 * @param shipping - Shipping Amount
 * @param paymentMethod - Checkout payment method key (e.g. card, paypal, invoice).
 * @param savedMethodUsed - Whether Checkout V4 or Store Checkout used a saved vault method.
 */
export const captureCheckoutCompleted = (
  orderId: number,
  productIds: string,
  subtotal: number,
  total: number,
  shipping: number,
  managerName: string,
  managerId: number,
  shippingType: string,
  guarantee: boolean,
  paymentMethod?: string,
  savedMethodUsed?: boolean
) => {
  /** If the manager is not Admin Site (ID = 2), attaching the user manager property */
  const userManager = managerId !== 2 && managerName ? managerName : 'N/A';

  MixpanelUtils.sendEvent('Checkout Completed', {
    Source: 'HQ',
    'Site Type': 'HQ',
    Type: 'Regular Order',
    Id: orderId,
    'Product Style Code': productIds,
    Subtotal: subtotal,
    Total: total,
    Shipping: shipping,
    'Shipping Type': shippingType,
    DT: getDtAbTestVersionForMixpanel(),
    'User manager': userManager,
    Guarantee: guarantee,
    ...(paymentMethod ? { 'Payment Type': resolveCheckoutPaymentType(paymentMethod) } : {}),
    ...(savedMethodUsed !== undefined ? { 'Saved Method Used': savedMethodUsed } : {}),
  });
};

export const captureOrderDetailsViewed = (orderId: number, status: string, name: string) => {
  MixpanelUtils.sendEvent('Order Details Viewed', {
    Source: 'HQ',
    Id: orderId,
    Status: status,
    Name: name,
    Page: 'Order Details',
    DT: getDtAbTestVersionForMixpanel(),
  });
};

export const captureProofDetailsViewed = (proofId: number, status: string, name: string) => {
  MixpanelUtils.sendEvent('Proof Details Viewed', {
    Id: proofId,
    Status: status,
    Name: name,
    Page: 'Proof Details',
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track the user try to complete the payment.
 *
 * @param id - Invoice Id.
 */
export const captureInvoicePaid = (id: number, isFullPayment: boolean) => {
  MixpanelUtils.sendEvent('Invoice Paid', {
    Id: id,
    'Full Payment': isFullPayment,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track when the user try to view the all pages.
 *
 * @param page - The page name. (All Designs Viewed | All Orders Viewed | All Invoices Viewed | All Favorite Designs Viewed | All Favorite Products Viewed)
 */
export const captureSeeAllPages = (page: string) => {
  MixpanelUtils.sendEvent(page, {
    Page: page,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track when the user clicks on stock alert options (Switch Color or Switch Product).
 *
 * @param productStyleCode - Product Style Code
 * @param color - Color name (actual color, not hex code)
 * @param source - Source (Design Tool Product Page, Design Tool, HQ)
 * @param message - The message displayed to the user
 * @param option - The option selected (Switch Color or Switch Product)
 * @param productBrand - Product Brand
 * @param section - Section (HQ, HQ Recent Designs, HQ Cart, HQ Checkout, Design Tool, DT Cart, DT Chatbot, DT Tab)
 */
export const captureStockAlertOptionClicked = (
  productStyleCode: string,
  color: string,
  source: string,
  message: string,
  option: string,
  productBrand: string,
  section: string
) => {
  MixpanelUtils.sendEvent('Stock Alert Option Clicked', {
    'Product Style Code': productStyleCode,
    Color: color,
    Source: source,
    'Site Type': 'HQ',
    ...(section === MIXPANEL_HQ_CART_SECTION && { Type: 'Regular Cart' }),
    Message: message,
    Option: option,
    'Product Brand': productBrand,
    Section: section,
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track when a user clicks on a similar product.
 *
 * @param sourceStyleCode - The style code of the current/source product
 * @param sourceColor - The color of the current/source product
 * @param sourceBrand - The brand of the current/source product
 * @param newStyleCode - The style code of the clicked similar product
 * @param newColor - The color of the clicked similar product
 * @param newBrand - The brand of the clicked similar product
 */
export const captureSimilarProductClicked = (
  sourceStyleCode: string,
  sourceColor: string,
  sourceBrand: string,
  newStyleCode: string,
  newColor: string,
  newBrand: string
) => {
  MixpanelUtils.sendEvent('Similar Product Clicked', {
    'Product Style Code': sourceStyleCode,
    Color: sourceColor,
    'Product Brand': sourceBrand,
    'New Product Style Code': newStyleCode,
    'New Product Color': newColor,
    'New Product Brand': newBrand,
    Source: 'HQ',
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track when a user clicks the "See More" button in similar products.
 *
 * @param sourceStyleCode - The style code of the current/source product
 * @param sourceColor - The color of the current/source product
 * @param sourceBrand - The brand of the current/source product
 */
export const captureSeeMoreSimilarProductsClicked = (
  sourceStyleCode: string,
  sourceColor: string,
  sourceBrand: string
) => {
  MixpanelUtils.sendEvent('See More Similar Products Clicked', {
    'Product Style Code': sourceStyleCode,
    Color: sourceColor,
    'Product Brand': sourceBrand,
    Source: 'HQ',
    DT: getDtAbTestVersionForMixpanel(),
  });
};

/**
 * Fire the event to track when a user acknowledges the restock date on the checkout page.
 *
 * @param styleCode - The product style code
 * @param color - The product color
 * @param source - The source of the event (default: 'HQ')
 * @param proofId - The proof ID
 * @param proofItemId - The proof item ID
 */
export const captureRestockDateAcknowledged = (
  styleCode: string,
  color: string,
  source: string,
  proofId?: string,
  proofItemId?: string
) => {
  MixpanelUtils.sendEvent('Restock Date Acknowledged', {
    'Product Style Code': styleCode,
    Color: color,
    Source: source,
    'Proof ID': proofId,
    'Proof Item ID': proofItemId,
    DT: getDtAbTestVersionForMixpanel(),
  });
};
